import { NgIf, NgIfContext } from '@angular/common';
import {
    ChangeDetectorRef,
    Directive,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
    ViewRef
} from '@angular/core';
import { Client } from '@df/core/service/client';
import { CdService } from '@df/ng/cd.service';
import difference from 'lodash-es/difference';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Creates directive that will work like ngIf with size arguments but simpler:
 *
 * ```html
 * <div *ifSize="'SL'"></div>
 * <div *ifSize="'!M'"></div>
 * <div *ifSize="'TSM'"></div>
 * ```
 *
 * IMPORTANT
 * You can pass T for touchy devices or !T for non touchy
 */
@Directive({
    selector: '[ifSize]',
    standalone: true
})
export class IfSizeDirective extends NgIf implements OnInit, OnChanges, OnDestroy {
    private readonly destroyed$ = new Subject<void>();
    private _lastConditionValue: boolean = false;

    /**
     * Allowed sizes
     */
    private _sizes: string[] = [];

    /**
     * Forced mode used with T option
     */
    private _forced = false;

    @Input()
    ifSize!: string;

    constructor(
        viewContainerRerf: ViewContainerRef,
        templateRef: TemplateRef<NgIfContext<unknown>>,
        private changeDetectorRef: ChangeDetectorRef,
        private readonly client: Client,
        private cdService: CdService
    ) {
        super(viewContainerRerf, templateRef);
    }

    ngOnInit(): void {
        this.client.obs.breakpoint.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            if (this.updateView() && !(<ViewRef>this.changeDetectorRef).destroyed) {
                this.cdService.push(this.changeDetectorRef);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        const ifSizeChange = changes['ifSize'];
        if (ifSizeChange && ifSizeChange.currentValue !== ifSizeChange.previousValue) {
            let ifSize = ifSizeChange.currentValue;

            if (typeof ifSize !== 'string') {
                ifSize = '';
            }

            const ALL = ['S', 'M', 'L', 'X'];
            const sizeString = ifSize.replace(' ', '').replace(',', '').toUpperCase();
            let sizeValues: string[];
            const invert = sizeString[0] === '!';

            if (invert) {
                sizeValues = sizeString.substr(1).split('');
                this._sizes = difference(ALL, sizeValues);
            } else {
                this._sizes = sizeValues = sizeString.split('');
            }

            // if T options is selected then we check if its matched - that is not gonna change later
            // so we will skip further updates
            this._forced = sizeValues.includes('T') && (this.client.isTouchy ? !invert : invert);

            this.updateView();
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    get condition(): boolean {
        return this._forced || this._sizes.includes(this.client.size);
    }

    /**
     * Applies condition
     */
    protected updateView(): boolean {
        const condition = this.condition;
        if (this._lastConditionValue === condition) {
            return false;
        }

        this.ngIf = this._lastConditionValue = condition;

        return true;
    }
}
